import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";
import { UseMutationsParams, AlertSeverityEnum } from "@/shared/interfaces";
import { erpApi } from "@/shared/axios";
import { toApiError } from "@/shared/utils/general";
import { useUiStore } from "@/store/ui";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { urlBackend } from "@/shared/constants";
import {
  TipoMetrica,
  TipoMetricaForm,
  TipoMetricaParams,
  TipoMetricaPagingResp,
} from "@/erp/controlcambios/tipo-metrica/types/tipoMetrica.interface";

const { get, post, put, remove, patch } = erpApi();
const BASE = `${urlBackend}api/v1/cambios/tipos-metrica`;

// listar
export const useFetchGetTiposMetrica = (
  params?: TipoMetricaParams,
  enabled = true
) =>
  useQuery({
    queryKey: ["tipos-metrica", ...Object.values(params ?? {})],
    queryFn: async () => getTiposMetrica(params),
    enabled,
  });

export const getTiposMetrica = async (
  params?: TipoMetricaParams
): Promise<TipoMetricaPagingResp> => {
  const q = getUrlParamsFetch(params ?? {});
  return (await get<TipoMetricaPagingResp>(`${BASE}?${q}`, true)) as unknown as TipoMetricaPagingResp;
};

// obtener por id
export const useFetchGetByIdTipoMetrica = (id: string) =>
  useQuery({
    queryKey: ["tipo-metrica", id],
    queryFn: async () => getByIdTipoMetrica(id),
    refetchOnWindowFocus: false,
  });

export const getByIdTipoMetrica = async (id: string) =>
  await get<TipoMetrica>(`${BASE}/${id}`, true);

// crear
export const useFetchPostTipoMetrica = ({
  returnUrl,
  navigate,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (data: TipoMetricaForm) => postTipoMetrica(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-metrica"] });
      navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlerta &&
        setAlerta(
          true,
          customSuccessMessage ?? "Tipo de métrica creado correctamente",
          AlertSeverityEnum.success
        );
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const postTipoMetrica = async (data: TipoMetricaForm) =>
  await post<TipoMetrica>(BASE, data, true);

// actualizar
export const useFetchPutTipoMetrica = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: putTipoMetrica,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tipos-metrica"] });
      queryClient.invalidateQueries({ queryKey: ["tipo-metrica", variables.id] });
      setAlerta(true, "Tipo de métrica actualizado correctamente", AlertSeverityEnum.success);
      navigate(returnUrl);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const putTipoMetrica = async ({
  id,
  data,
}: {
  id: string;
  data: Omit<TipoMetricaForm, "tipoMetricaId">;
}) => await put<TipoMetrica>(`${BASE}/${id}`, data, true);

// eliminar
export const useFetchDeleteTipoMetrica = () => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (id: string) => deleteTipoMetrica(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-metrica"] });
      setAlerta(true, "Tipo de métrica eliminado correctamente", AlertSeverityEnum.success);
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const deleteTipoMetrica = async (id: string) =>
  await remove<TipoMetrica>(`${BASE}/${id}`, true);

// cambiar estado
export const useFetchPatchEstadoTipoMetrica = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchEstadoTipoMetrica,
    onSuccess: () => {
      toast.success("Estado del tipo de métrica actualizado");
      queryClient.invalidateQueries({ queryKey: ["tipos-metrica"] });
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const patchEstadoTipoMetrica = async ({ id, estado }: { id: string; estado: boolean }) =>
  await patch(`${BASE}/${id}/estado?estado=${estado}`, undefined, true);
